import { darfStarten } from '@netcon-work/shared'
import { AufgabenVerweis } from '../AufgabeKontext'
import { useNehmen, useStarten } from '../../lib/mutationen'
import { Fristwort } from './Fristwort'
import { WiedervorlageMarke } from './WiedervorlageMarke'
import type { Naechste } from './lage'
import { Button, SectionTitle } from '../ui/primitives'
import { AufgabenWeltMarke, weltenGemischt } from '../WorkspaceMarke'

/** Höchstens so viele stehen offen da. Der Rest steht im Fenster dahinter. */
const SICHTBAR = 3

/**
 * **Zone 3 – der Kopf der Rangfolge** ([Z-06](../../../../../docs/phasen/zusatz-06-nehmen-und-starten.md)).
 *
 * Hieß bis zum 24.08.2026 „Nebenbei" und zeigte nur den Vorrat. Daneben stand links die eigene
 * Arbeit – und ein Entwickler musste selbst entscheiden, welche der beiden Listen gerade die
 * richtige ist. Jetzt ist es **eine** Liste: dieselbe Reihenfolge wie im Fenster, hier nur die
 * ersten drei.
 *
 * Die Marke sagt, welcher Knopf gilt:
 *
 * - **●** gehört dir → nur *Starten*
 * - **★** / ohne → *Nehmen* (nie gesperrt) und *Starten* (belegt einen Fokus-Platz)
 *
 * Bewusst schmal und auf drei gedeckelt: eine Seitenleiste mit 28 Karten ist keine Hilfe,
 * sondern eine Aufforderung zu 28 Entscheidungen. Darunter steht der Knopf, der aus 28
 * Entscheidungen eine macht.
 */
export function AlsNaechstes({
  rangfolge,
  wip,
  wipLimit,
  fokusFuer,
  wipHinweisText,
  hinweis,
  fensterOeffnen,
}: {
  rangfolge: readonly Naechste[]
  /** Der Stand der **aktiven** Welt – für die Zeile „Fokus voll" unter der Liste. */
  wip: number
  wipLimit: number
  /**
   * **Die Grenze je Zeile** ([E-274](../../../../../docs/ENTSCHEIDUNGEN.md#e-274)) – seit der
   * Vorrat über alle Arbeitswelten geht, gilt je Aufgabe die ihrer eigenen Welt. Dieselbe
   * Rechnung wie in `Vorlauf` und im Server.
   */
  fokusFuer: (workspaceId: string) => { wip: number; wipLimit: number }
  /** Woher das Limit kommt – nur gesetzt, wenn überhaupt eines gilt (Z-07.13). */
  wipHinweisText: string | null
  hinweis: string | null
  fensterOeffnen: () => void
}) {
  const nehmen = useNehmen()
  const starten = useStarten()
  const sichtbar = rangfolge.slice(0, SICHTBAR)
  /**
   * **Über die ganze Rangfolge gefragt, nicht über die drei sichtbaren** (Z-07.8). Sonst
   * erschiene die Marke, sobald man das Fenster öffnet, und verschwände beim Schließen wieder –
   * dieselbe Liste, zwei Auskünfte.
   */
  const gemischt = weltenGemischt(rangfolge)

  return (
    <section>
      <SectionTitle hint="die Reihenfolge, aus der du arbeitest">Als Nächstes</SectionTitle>

      <div className="rounded-card border border-line2 bg-surface px-3.5 py-3 shadow-task">
        {sichtbar.length === 0 && (
          <p className="py-1 text-center text-[12.5px] text-muted">
            Nichts offen und nichts im Vorrat.
          </p>
        )}

        {sichtbar.map((a) => {
          const eigen = a.marke === 'bei_dir'
          const fokus = fokusFuer(a.workspaceId)
          const pruefung = darfStarten({
            inArbeit: fokus.wip,
            wipLimit: fokus.wipLimit,
            poolDurchbrichtWip: a.pool?.wipDurchbrechen ?? false,
          })
          return (
            /**
             * **Auf dem Telefon zwei Zeilen, am Rechner eine**
             * ([Z-10](../../../../../docs/phasen/zusatz-10-mobil.md)).
             *
             * Die Zeile trug drei Dinge nebeneinander, von denen zwei nicht schrumpfen: die
             * Pool-Marke („🎫 Tickets & Kleinigkeiten", rund 200 px) und die beiden Knöpfe
             * (rund 130 px). Auf 358 px nutzbarer Breite blieb dem Titel **nichts** – er fiel
             * auf ein Wort je Zeile, und die Knöpfe legten sich darüber.
             *
             * Deshalb unter `sm` ein Umbruch, und der Titel geht per `order-first` **nach
             * oben**: er ist das, wonach man sucht. Marke und Knöpfe teilen sich die zweite
             * Zeile – zusammen 340 px, das passt. Ab `sm` steht wieder alles nebeneinander,
             * unverändert.
             */
            <div
              key={a.id}
              className="flex flex-wrap items-start gap-2.5 border-b border-line py-2.5 last:border-b-0 sm:flex-nowrap"
            >
              <span
                className={`max-w-full min-w-0 shrink-0 truncate rounded-md px-[7px] py-[2px] text-[10.5px] font-semibold ${
                  eigen
                    ? 'bg-accent-soft text-accent-deep'
                    : a.pool?.wipDurchbrechen
                      ? 'bg-pool-sofort-bg text-pool-sofort'
                      : 'bg-pool-tickets-bg text-pool-tickets'
                }`}
                title={eigen ? 'Gehört dir – nur noch anfangen.' : (a.pool?.regeln ?? undefined)}
              >
                {eigen ? '● gehört dir' : `${a.pool?.icon} ${a.pool?.label}`}
              </span>
              {a.wiedervorlage && <WiedervorlageMarke tag={a.wiedervorlage} />}
              <span className="order-first w-full min-w-0 text-[13px] leading-snug sm:order-none sm:w-auto sm:flex-1">
                <AufgabenVerweis
                  aufgabeId={a.id}
                  className="block cursor-pointer text-left hover:text-accent hover:underline"
                >
                  {a.title}
                </AufgabenVerweis>
                <small className="mt-0.5 flex flex-wrap items-center gap-x-1 text-[10.5px] text-muted">
                  {/** Z-07.8 – siehe `AufgabenWeltMarke`: erscheint nur bei mehreren Welten. */}
                  <AufgabenWeltMarke workspaceId={a.workspaceId} zeigen={gemischt} />
                  {a.vorhabenLabel ? `${a.vorhabenLabel} · ` : ''}
                  <Fristwort frist={a.frist} zeitfenster={a.zeitfenster} />
                  {/*
                    Der Wunsch steht am Knopf, nicht im Detail: er ändert die Entscheidung, die
                    genau hier getroffen wird. Wer ihn erst nach dem Öffnen sieht, hat schon
                    genommen.
                  */}
                  {a.wunschIds.length > 0 && ` · ★ ${a.wunschNamen}`}
                  {/*
                    Wer sie schon hat, gehört dazu: eine Sofort-Störung sieht man nur noch, wenn
                    sie einem selbst zugedacht ist (`sofortSichtbarFuer`) – dann ist „liegt bei
                    Davin" die Erklärung, warum sie überhaupt noch dasteht. Bei Eigenem wäre es
                    der eigene Name und damit keine Auskunft.
                  */}
                  {!eigen && a.bearbeiterName && ` · liegt bei ${a.bearbeiterName}`}
                </small>
              </span>
              {/* `ml-auto` schiebt die Knöpfe in der Telefon-Zeile nach rechts, neben die Marke.
                  Ab `sm` nimmt der Titel mit `flex-1` den Platz und der Schub ist überflüssig. */}
              <span className="ml-auto flex shrink-0 gap-1 sm:ml-0">
                {/*
                  Kein `disabled` am Nehmen – es ist der Handgriff, der auch bei vollem Fokus
                  geht. Genau das ist der Unterschied, den Z-06 eingeführt hat.
                */}
                {!eigen && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => nehmen.mutate({ id: a.id })}
                    aria-label={`${a.title} nehmen`}
                  >
                    Nehmen
                  </Button>
                )}
                <Button
                  size="sm"
                  variant={eigen ? 'primary' : 'ghost'}
                  disabled={!pruefung.erlaubt}
                  onClick={() => starten.mutate({ id: a.id })}
                  aria-label={`${a.title} starten`}
                  {...(pruefung.grund ? { title: pruefung.grund } : {})}
                >
                  Starten
                </Button>
              </span>
            </div>
          )
        })}

        <Button
          variant="ghost"
          onClick={fensterOeffnen}
          className="mt-3 w-full justify-center"
          title="Die ganze Reihenfolge – mit Begründung je Vorschlag."
        >
          Alle {rangfolge.length} ansehen
        </Button>

        {/*
          Sichtbar und nicht nur im `title`: der Grund, warum die Starten-Knöpfe grau sind,
          gehört auf den Bildschirm. Ein Tooltip sieht nur, wer schwebt – wer klickt, sieht
          nichts und hält die Ansicht für kaputt.
        */}
        {Number.isFinite(wipLimit) && wip >= wipLimit && (
          <p className="mt-2 text-center text-[11.5px] font-semibold text-warn">
            Fokus voll ({wip} von {wipLimit}) – Starten ist gesperrt, Nehmen nicht. 🔥 Sofort geht
            trotzdem.
            {/**
             * **Woher die Zahl kommt** (Z-07.13). Ohne diesen Satz steht dort „3 von 3", während
             * in dieser Arbeitswelt nur eine läuft – die anderen zwei liegen woanders, und der
             * Leitstand zeigt nie etwas Übergeordnetes ([E-235]). Er nennt **dass**, nicht
             * **wo**: der andere Workspace könnte ein persönlicher sein.
             */}
            {wipHinweisText && <span className="block font-normal">{wipHinweisText}</span>}
          </p>
        )}

        {hinweis && <p className="mt-2 text-center text-[11.5px] text-muted">{hinweis}</p>}
      </div>
    </section>
  )
}
